import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLLM } from "@/contexts/LLMContext";
import { normalizeError, theses as thesesApi } from "@/lib/api";
import { projectType } from "@/lib/projectTypes";

/**
 * Adds a top-level division to the open project.
 *
 * What that division is called follows the document: a thesis and a volume get
 * chapters, an article or a proposal gets paragraphs. The modal cannot be
 * reached without an open project, but it checks anyway rather than posting to
 * an undefined id.
 */
export default function NewChapterModal({ open, onOpenChange, thesis, onChapterCreated }) {
  const { showError } = useLLM();
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(false);

  const type = useMemo(() => projectType(thesis?.project_type), [thesis?.project_type]);
  const unit = type.unit;
  const unitLower = unit.toLowerCase();

  const existing = thesis?.chapters?.length || 0;
  const placeholder = type.id === "thesis" || type.id === "volume"
    ? `es. Capitolo ${existing || 1} - Metodologia`
    : "es. Risultati preliminari";

  useEffect(() => {
    if (open) {
      setTitle("");
      setLoading(false);
    }
  }, [open]);

  const create = async (e) => {
    e?.preventDefault();
    if (!thesis?.thesis_id) {
      toast.error("Nessun progetto aperto.");
      onOpenChange(false);
      return;
    }
    const clean = title.trim();
    if (!clean) {
      toast.error(`Inserisci il titolo del ${unitLower}.`);
      return;
    }
    setLoading(true);
    try {
      const result = await thesesApi.createChapter(thesis.thesis_id, { title: clean });
      toast.success(`${unit} creato.`);
      onChapterCreated(result.thesis);
      onOpenChange(false);
    } catch (err) {
      showError(normalizeError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !loading && onOpenChange(v)}>
      <DialogContent className="sm:max-w-[480px]" data-testid="new-chapter-modal">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-xl">Nuovo {unitLower}</DialogTitle>
          <DialogDescription className="numa-meta">
            Verrà aggiunto in coda a «{thesis?.title}».
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={create} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="chapter-title">Titolo</Label>
            <Input
              id="chapter-title"
              data-testid="new-chapter-title"
              placeholder={placeholder}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-10"
              disabled={loading}
              autoFocus
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Annulla
            </Button>
            <Button type="submit" data-testid="confirm-new-chapter" disabled={loading || !title.trim()}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Crea {unitLower}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
